// One timer, one answer, however many tabs and screens are looking at it.
//
// Every control in the app already shouts `pm-timer-changed` when it starts or stops something,
// and every control listens — so within one tab they agree. Nothing carried that next door: pause
// it on the workflow page and a second tab open on Triage went on showing a running clock until
// its own poll came round, which is most of a minute of a timer that says it is counting when it
// is not.
//
// So the same news, sent to the other tabs on this device. localStorage is the carrier because a
// write to it raises `storage` in every other tab of the same origin and in none of the tab that
// wrote it, which is exactly the shape wanted — and it works in every browser this ships to,
// unlike a BroadcastChannel in an older Safari.
const KEY = 'hub.timer.changed';

// News that came from next door must not be sent back, or two tabs echo at each other for ever.
let echoing = false;

export function installTimerBridge() {
  const onLocal = () => {
    if (echoing) return;
    try { window.localStorage.setItem(KEY, String(Date.now())); } catch { /* a browser with storage off still works within its own tab */ }
  };
  const onRemote = (e) => {
    if (!e || e.key !== KEY) return;
    echoing = true;
    // Synchronous, so the flag is still up while every listener runs and back down before
    // anything else can write.
    try { window.dispatchEvent(new Event('pm-timer-changed')); } finally { echoing = false; }
  };
  window.addEventListener('pm-timer-changed', onLocal);
  window.addEventListener('storage', onRemote);
  return () => {
    window.removeEventListener('pm-timer-changed', onLocal);
    window.removeEventListener('storage', onRemote);
  };
}

// What the timer has actually counted: wall clock since it started, less every finished pause, and
// frozen where the current one began.
//
// The same sum as `timerWorkedMs` on the server and as the banner's own, which is the point of it
// being here: the banner had it right, the button on a task did not — it counted plain wall clock
// and went on climbing through a pause, which is what "paused but still counting" looked like to
// whoever reported it. Two copies of one sum is one too many.
export const parseServerTime = (s) => (s ? new Date(String(s).replace(' ', 'T') + 'Z').getTime() : 0);

export function workedMsOf(timer, nowMs = Date.now()) {
  if (!timer || !timer.started_at) return 0;
  const started = parseServerTime(timer.started_at);
  if (!started) return 0;
  const pausedAt = timer.paused_at ? parseServerTime(timer.paused_at) : null;
  return Math.max(0, (pausedAt || nowMs) - started - (Number(timer.paused_ms) || 0));
}
